import React from 'react';
import {
    Modal,
    Stack,
    Text,
    Checkbox,
    Textarea,
    Alert,
    Button,
    Group,
    Badge,
    Box,
    Divider,
    Loader,
    Center,
} from '@mantine/core';
import { IconAlertTriangle, IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useCoMedicationChecklist } from '../../../../global/entities/OTP/queries/useCoMedicationChecklist';
import { useSaveCoMedicationChecklist } from '../../../../global/entities/OTP/mutations/useSaveCoMedicationChecklist';
import { ICoMedicationChecklistItem, CoMedicationWarningLevel } from '../../../../global/entities/OTP/OTP';

interface ICoMedicationChecklistModalProps {
    opened: boolean;
    onClose: () => void;
    otpId: number;
    onSubmitSuccess: () => void;
    readOnly?: boolean;
}

const getWarningIcon = (level: string) => {
    switch (level) {
        case CoMedicationWarningLevel.Severe:
            return <IconAlertTriangle size={18} color="var(--mantine-color-red-6)" />;
        case CoMedicationWarningLevel.Warning:
            return <IconAlertCircle size={18} color="var(--mantine-color-orange-6)" />;
        case CoMedicationWarningLevel.Caution:
            return <IconInfoCircle size={18} color="var(--mantine-color-yellow-6)" />;
        default:
            return null;
    }
};

const getWarningColor = (level: string): string => {
    switch (level) {
        case CoMedicationWarningLevel.Severe:
            return 'red';
        case CoMedicationWarningLevel.Warning:
            return 'orange';
        case CoMedicationWarningLevel.Caution:
            return 'yellow';
        default:
            return 'gray';
    }
};

export const CoMedicationChecklistModal: React.FC<ICoMedicationChecklistModalProps> = ({
    opened,
    onClose,
    otpId,
    onSubmitSuccess,
    readOnly = false,
}) => {
    const { t } = useTranslation();
    const { data: checklist, isLoading } = useCoMedicationChecklist(opened ? otpId : undefined);
    const saveChecklistMutation = useSaveCoMedicationChecklist();

    const [items, setItems] = React.useState<ICoMedicationChecklistItem[]>([]);
    const [allReviewed, setAllReviewed] = React.useState(false);

    // Initialize items when checklist data is loaded
    React.useEffect(() => {
        if (checklist?.items) 
            setItems(checklist.items);
        
    }, [checklist]);

    // Reset state when modal closes
    React.useEffect(() => {
        if (!opened) 
            setAllReviewed(false);
        
    }, [opened]);

    const handleItemChange = (index: number, updates: Partial<ICoMedicationChecklistItem>) => {
        setItems((prev) =>
            prev.map((item, i) => (i === index ? { ...item, ...updates } : item))
        );
    };

    // Check if all severe warnings are acknowledged
    const allSevereAcknowledged = items
        .filter((item) => item.isPresent && item.warningLevel === CoMedicationWarningLevel.Severe)
        .every((item) => item.warningAcknowledged);

    const canSubmit = allReviewed && allSevereAcknowledged;

    const handleSubmit = async () => {
        try {
            await saveChecklistMutation.mutateAsync({
                otpId,
                request: {
                    items,
                    acknowledgeAllWarnings: allReviewed,
                },
            });
            onSubmitSuccess();
        } catch {
            // Error handled by mutation hook
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <Group gap="xs">
                    <IconAlertTriangle size={20} color="var(--mantine-color-orange-6)" />
                    <Text fw={600}>{t('otp.coMedication.modal.title', 'Pre-Submission Safety Checklist')}</Text>
                </Group>
            }
            size="lg"
            closeOnClickOutside={false}
        >
            {isLoading ? (
                <Center py="xl">
                    <Loader />
                </Center>
            ) : (
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        {t(
                            'otp.coMedication.modal.description',
                            "Review patient's current co-medications for potential interactions with opioid tapering:"
                        )}
                    </Text>

                    {items.map((item, index) => (
                        <Box
                            key={item.medicationType}
                            p="sm"
                            style={{
                                border: '1px solid var(--mantine-color-gray-3)',
                                borderRadius: 'var(--mantine-radius-sm)',
                                backgroundColor: item.isPresent
                                    ? `var(--mantine-color-${getWarningColor(item.warningLevel)}-0)`
                                    : undefined,
                            }}
                        >
                            <Stack gap="xs">
                                <Group justify="space-between">
                                    <Checkbox
                                        label={
                                            <Group gap="xs">
                                                <Text fw={500}>
                                                    {t(
                                                        `otp.coMedication.types.${item.medicationType}`,
                                                        item.medicationType
                                                    )}
                                                </Text>
                                                {item.warningLevel !== CoMedicationWarningLevel.None && (
                                                    <Badge
                                                        size="xs"
                                                        color={getWarningColor(item.warningLevel)}
                                                        variant="light"
                                                    >
                                                        {item.warningLevel}
                                                    </Badge>
                                                )}
                                            </Group>
                                        }
                                        checked={item.isPresent}
                                        disabled={readOnly}
                                        onChange={(e) =>
                                            handleItemChange(index, {
                                                isPresent: e.currentTarget.checked,
                                                warningAcknowledged: false,
                                            })
                                        }
                                    />
                                    {getWarningIcon(item.warningLevel)}
                                </Group>

                                {item.isPresent && (
                                    <>
                                        {item.warningMessage && (
                                            <Alert
                                                color={getWarningColor(item.warningLevel)}
                                                variant="light"
                                                icon={getWarningIcon(item.warningLevel)}
                                                py="xs"
                                            >
                                                <Text size="sm">{item.warningMessage}</Text>
                                            </Alert>
                                        )}

                                        {item.medicationType === 'Other' && (
                                            <Textarea
                                                placeholder={t(
                                                    'otp.coMedication.notes',
                                                    'Additional notes (optional)'
                                                )}
                                                value={item.notes || ''}
                                                readOnly={readOnly}
                                                onChange={(e) =>
                                                    handleItemChange(index, {
                                                        notes: e.currentTarget.value,
                                                    })
                                                }
                                                size="sm"
                                                minRows={2}
                                            />
                                        )}

                                        {item.warningLevel === CoMedicationWarningLevel.Severe && (
                                            <Checkbox
                                                label={
                                                    <Text size="sm" c="red" fw={500}>
                                                        {t(
                                                            'otp.coMedication.acknowledgeWarning',
                                                            'I acknowledge this contraindication and have assessed the risk'
                                                        )}
                                                    </Text>
                                                }
                                                checked={item.warningAcknowledged}
                                                disabled={readOnly}
                                                onChange={(e) =>
                                                    handleItemChange(index, {
                                                        warningAcknowledged: e.currentTarget.checked,
                                                    })
                                                }
                                                color="red"
                                            />
                                        )}
                                    </>
                                )}
                            </Stack>
                        </Box>
                    ))}

                    <Divider />

                    {readOnly ? (
                        <Group justify="flex-end" mt="md">
                            <Button variant="outline" color="gray" onClick={onClose}>
                                {t('button.close', 'Close')}
                            </Button>
                        </Group>
                    ) : (
                        <>
                            <Checkbox
                                label={
                                    <Text size="sm" fw={500}>
                                        {t(
                                            'otp.coMedication.acknowledgeAll',
                                            'I have reviewed all co-medications and contraindications for this patient'
                                        )}
                                    </Text>
                                }
                                checked={allReviewed}
                                onChange={(e) => setAllReviewed(e.currentTarget.checked)}
                            />

                            <Group justify="flex-end" mt="md">
                                <Button variant="outline" color="gray" onClick={onClose}>
                                    {t('button.cancel', 'Cancel')}
                                </Button>
                                <Button
                                    color="blue"
                                    onClick={handleSubmit}
                                    disabled={!canSubmit}
                                    loading={saveChecklistMutation.isPending}
                                >
                                    {t('otp.coMedication.submitForValidation', 'Submit for Validation')}
                                </Button>
                            </Group>
                        </>
                    )}
                </Stack>
            )}
        </Modal>
    );
};
